import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'

export default function TemplateForm({ template, onSubmit, onCancel, loading }) {
  // Parse existing ClickUrl into components
  const parseClickUrl = (url) => {
    if (!url) return { baseUrl: '', utmSource: '', utmMedium: '', utmCampaign: '', utmContent: '' }
    try {
      const urlObj = new URL(url)
      const params = urlObj.searchParams
      // Remove UTM params from URL to get base
      params.delete('utm_source')
      params.delete('utm_medium')
      params.delete('utm_campaign')
      params.delete('utm_content')
      const baseUrl = urlObj.origin + urlObj.pathname + (params.toString() ? '?' + params.toString() : '')

      const originalParams = new URL(url).searchParams
      return {
        baseUrl: baseUrl.replace(/\?$/, ''),
        utmSource: originalParams.get('utm_source') || '',
        utmMedium: originalParams.get('utm_medium') || '',
        utmCampaign: originalParams.get('utm_campaign') || '',
        utmContent: originalParams.get('utm_content') || ''
      }
    } catch {
      return { baseUrl: url, utmSource: '', utmMedium: '', utmCampaign: '', utmContent: '' }
    }
  }

  const parsedUrl = parseClickUrl(template?.ClickUrl)

  const [formData, setFormData] = useState({
    Name: template?.Name || '',
    Title: template?.Title || '',
    Body: template?.Body || '',
    baseUrl: parsedUrl.baseUrl,
    utmSource: parsedUrl.utmSource || 'push',
    utmMedium: parsedUrl.utmMedium || 'notification',
    utmCampaign: parsedUrl.utmCampaign,
    utmContent: parsedUrl.utmContent
  })
  const [errors, setErrors] = useState({})
  const [utmAccordionOpen, setUtmAccordionOpen] = useState(false)
  const [existingUtms, setExistingUtms] = useState({ sources: [], mediums: [], campaigns: [], contents: [] })

  // Fetch existing UTM values from templates
  useEffect(() => {
    const fetchExistingUtms = async () => {
      const { data } = await supabase
        .from('PushTemplates')
        .select('ClickUrl')
        .eq('IsDeleted', false)
        .not('ClickUrl', 'is', null)

      if (data) {
        const sources = new Set()
        const mediums = new Set()
        const campaigns = new Set()
        const contents = new Set()

        data.forEach(t => {
          try {
            const url = new URL(t.ClickUrl)
            const params = url.searchParams
            if (params.get('utm_source')) sources.add(params.get('utm_source'))
            if (params.get('utm_medium')) mediums.add(params.get('utm_medium'))
            if (params.get('utm_campaign')) campaigns.add(params.get('utm_campaign'))
            if (params.get('utm_content')) contents.add(params.get('utm_content'))
          } catch {}
        })

        setExistingUtms({
          sources: [...sources].sort(),
          mediums: [...mediums].sort(),
          campaigns: [...campaigns].sort(),
          contents: [...contents].sort()
        })
      }
    }
    fetchExistingUtms()
  }, [])

  // Build full Click URL from components
  const buildClickUrl = () => {
    if (!formData.baseUrl) return ''
    try {
      const url = new URL(formData.baseUrl)
      if (formData.utmSource) url.searchParams.set('utm_source', formData.utmSource)
      if (formData.utmMedium) url.searchParams.set('utm_medium', formData.utmMedium)
      if (formData.utmCampaign) url.searchParams.set('utm_campaign', formData.utmCampaign)
      if (formData.utmContent) url.searchParams.set('utm_content', formData.utmContent)
      return url.toString()
    } catch {
      return formData.baseUrl
    }
  }

  const validate = () => {
    const newErrors = {}
    if (!formData.Name.trim()) newErrors.Name = 'Name is required'
    if (!formData.baseUrl.trim()) newErrors.baseUrl = 'Base URL is required'

    // Validate base URL format
    if (formData.baseUrl.trim()) {
      try {
        new URL(formData.baseUrl.trim())
      } catch {
        newErrors.baseUrl = 'Must be a valid URL (e.g., https://example.com/page)'
      }
    }

    // UTM validation - all required except utm_content
    if (!formData.utmSource.trim()) newErrors.utmSource = 'UTM Source is required'
    if (!formData.utmMedium.trim()) newErrors.utmMedium = 'UTM Medium is required'
    if (!formData.utmCampaign.trim()) newErrors.utmCampaign = 'UTM Campaign is required'

    // UTM format validation (lowercase, no spaces, alphanumeric with underscores/hyphens)
    const utmPattern = /^[a-z0-9_-]+$/
    if (formData.utmSource && !utmPattern.test(formData.utmSource)) {
      newErrors.utmSource = 'Use lowercase letters, numbers, underscores, or hyphens only'
    }
    if (formData.utmMedium && !utmPattern.test(formData.utmMedium)) {
      newErrors.utmMedium = 'Use lowercase letters, numbers, underscores, or hyphens only'
    }
    if (formData.utmCampaign && !utmPattern.test(formData.utmCampaign)) {
      newErrors.utmCampaign = 'Use lowercase letters, numbers, underscores, or hyphens only'
    }
    if (formData.utmContent && !utmPattern.test(formData.utmContent)) {
      newErrors.utmContent = 'Use lowercase letters, numbers, underscores, or hyphens only'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return

    const submitData = {
      Name: formData.Name.trim(),
      Title: formData.Title.trim() || null,
      Body: formData.Body.trim() || null,
      ClickUrl: buildClickUrl()
    }

    const result = await onSubmit(submitData)
    if (result?.error) {
      setErrors({ submit: result.error.message })
    }
  }

  const handleChange = (field, value) => {
    // Auto-format UTM fields to lowercase and replace spaces with underscores
    if (field.startsWith('utm')) {
      value = value.toLowerCase().replace(/\s+/g, '_')
    }
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }))
    }
  }

  // Extract variables from title and body
  const extractVariables = () => {
    const text = `${formData.Title} ${formData.Body}`
    const matches = text.match(/\{\{(\w+)\}\}/g) || []
    return [...new Set(matches)]
  }

  const variables = extractVariables()
  const fullClickUrl = buildClickUrl()

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Template Name *
        </label>
        <input
          type="text"
          value={formData.Name}
          onChange={(e) => handleChange('Name', e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          placeholder="e.g., Welcome Notification"
        />
        {errors.Name && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.Name}</p>}
      </div>

      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Notification Title
        </label>
        <input
          type="text"
          value={formData.Title}
          onChange={(e) => handleChange('Title', e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          placeholder="e.g., Welcome to our app!"
        />
        {errors.Title && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.Title}</p>}
      </div>

      {/* Body */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Notification Body
        </label>
        <textarea
          value={formData.Body}
          onChange={(e) => handleChange('Body', e.target.value)}
          rows={3}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          placeholder="e.g., We're excited to have you here. Tap to get started!"
        />
        {errors.Body && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.Body}</p>}
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Use variables like {'{{username}}'} for dynamic content
        </p>
      </div>

      {/* Click URL Section */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-4">
        <h4 className="text-sm font-medium text-gray-900 dark:text-white">Click URL & UTM Parameters *</h4>

        {/* Base URL */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Base URL *
          </label>
          <input
            type="text"
            value={formData.baseUrl}
            onChange={(e) => handleChange('baseUrl', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
            placeholder="https://looplly.me/welcome"
          />
          {errors.baseUrl && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.baseUrl}</p>}
        </div>

        {/* UTM Parameters Grid */}
        <div className="grid grid-cols-2 gap-3">
          {/* UTM Source */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              utm_source *
            </label>
            <input
              type="text"
              value={formData.utmSource}
              onChange={(e) => handleChange('utmSource', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
              placeholder="push"
            />
            {errors.utmSource && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.utmSource}</p>}
            {existingUtms.sources.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {existingUtms.sources.slice(0, 3).map(s => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => handleChange('utmSource', s)}
                    className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-500"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* UTM Medium */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              utm_medium *
            </label>
            <input
              type="text"
              value={formData.utmMedium}
              onChange={(e) => handleChange('utmMedium', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
              placeholder="notification"
            />
            {errors.utmMedium && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.utmMedium}</p>}
            {existingUtms.mediums.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {existingUtms.mediums.slice(0, 3).map(m => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => handleChange('utmMedium', m)}
                    className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-500"
                  >
                    {m}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* UTM Campaign */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              utm_campaign *
            </label>
            <input
              type="text"
              value={formData.utmCampaign}
              onChange={(e) => handleChange('utmCampaign', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
              placeholder="welcome_series"
            />
            {errors.utmCampaign && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.utmCampaign}</p>}
            {existingUtms.campaigns.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {existingUtms.campaigns.slice(0, 3).map(c => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => handleChange('utmCampaign', c)}
                    className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-500"
                  >
                    {c}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* UTM Content */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              utm_content
            </label>
            <input
              type="text"
              value={formData.utmContent}
              onChange={(e) => handleChange('utmContent', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
              placeholder="cta_button"
            />
            {errors.utmContent && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.utmContent}</p>}
            {existingUtms.contents.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {existingUtms.contents.slice(0, 3).map(c => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => handleChange('utmContent', c)}
                    className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-500"
                  >
                    {c}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Generated URL Preview */}
        {fullClickUrl && (
          <div className="bg-gray-50 dark:bg-gray-900 rounded p-2">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Generated URL:</p>
            <p className="text-xs text-blue-600 dark:text-blue-400 break-all font-mono">{fullClickUrl}</p>
          </div>
        )}

        {/* UTM Explanation Accordion */}
        <div className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
          <button
            type="button"
            onClick={() => setUtmAccordionOpen(!utmAccordionOpen)}
            className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 flex items-center justify-between text-left"
          >
            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
              What are UTM parameters?
            </span>
            <svg
              className={`w-4 h-4 text-gray-500 transition-transform ${utmAccordionOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {utmAccordionOpen && (
            <div className="px-3 py-3 text-xs text-gray-600 dark:text-gray-400 space-y-3 bg-white dark:bg-gray-800">
              <p>
                UTM parameters are tags added to URLs to track the effectiveness of marketing campaigns in analytics tools like Google Analytics.
              </p>
              <div className="space-y-2">
                <div className="flex">
                  <span className="font-semibold text-gray-700 dark:text-gray-300 w-24 flex-shrink-0">utm_source</span>
                  <span>Identifies the traffic source (e.g., <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">push</code>, <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">email</code>, <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">sms</code>)</span>
                </div>
                <div className="flex">
                  <span className="font-semibold text-gray-700 dark:text-gray-300 w-24 flex-shrink-0">utm_medium</span>
                  <span>Marketing medium (e.g., <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">notification</code>, <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">banner</code>, <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">cpc</code>)</span>
                </div>
                <div className="flex">
                  <span className="font-semibold text-gray-700 dark:text-gray-300 w-24 flex-shrink-0">utm_campaign</span>
                  <span>Campaign name (e.g., <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">welcome_series</code>, <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">black_friday</code>)</span>
                </div>
                <div className="flex">
                  <span className="font-semibold text-gray-700 dark:text-gray-300 w-24 flex-shrink-0">utm_content</span>
                  <span>Differentiates similar content (e.g., <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">cta_button</code>, <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">hero_image</code>)</span>
                </div>
              </div>
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded p-2 mt-2">
                <p className="text-blue-800 dark:text-blue-200">
                  <strong>Naming convention:</strong> Use lowercase letters, numbers, underscores (_), or hyphens (-). No spaces allowed.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Detected Variables */}
      {variables.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Detected Variables
          </label>
          <div className="flex flex-wrap gap-2">
            {variables.map((variable, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200 text-xs font-mono rounded"
              >
                {variable}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Preview */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Preview
        </label>
        <div className="bg-gray-900 text-white p-4 rounded-lg max-w-sm">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 mt-1">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold">{formData.Title || 'Notification Title'}</p>
              <p className="text-xs text-gray-300 mt-1">{formData.Body || 'Notification body text...'}</p>
            </div>
          </div>
        </div>
      </div>

      {errors.submit && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-sm text-red-800 dark:text-red-200">{errors.submit}</p>
        </div>
      )}

      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition disabled:opacity-50 flex items-center space-x-2"
        >
          {loading && (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          )}
          <span>{template ? 'Update' : 'Create'} Template</span>
        </button>
      </div>
    </form>
  )
}
